import { useState, useEffect, useCallback, useRef } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { ticketingAPI } from '../tabs/ticketing/api/ticketing.api';

// Custom event name for syncing mention count across hook instances
const MENTION_COUNT_REFRESH_EVENT = 'ticket:mention-count-refresh';

interface TicketMentionEvent {
  ticket_id: string;
  ticket_number: number;
  ticket_title: string;
  comment_id: string;
  mentioned_by: {
    id: string;
    name: string;
  };
  timestamp: string;
}

interface UseTicketMentionCountReturn {
  mentionCount: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useTicketMentionCount = (): UseTicketMentionCountReturn => {
  const [mentionCount, setMentionCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { socket, isConnected } = useSocket();
  const isMounted = useRef(true);

  const fetchMentionCount = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await ticketingAPI.getUnreadMentionCount();
      if (isMounted.current && response.success) {
        setMentionCount(response.data.count);
      }
    } catch (err) {
      console.error('Error fetching mention count:', err);
      if (isMounted.current) {
        setError('Failed to fetch mention count');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Refetch and broadcast to other hook instances
  const refetchAndBroadcast = useCallback(async () => {
    await fetchMentionCount();
    // Dispatch custom event to sync other instances
    window.dispatchEvent(new CustomEvent(MENTION_COUNT_REFRESH_EVENT));
  }, [fetchMentionCount]);

  // Initial fetch
  useEffect(() => {
    isMounted.current = true;
    fetchMentionCount();

    return () => {
      isMounted.current = false;
    };
  }, [fetchMentionCount]);

  // Listen for custom event from other hook instances
  useEffect(() => {
    const handleRefreshEvent = () => {
      fetchMentionCount();
    };

    window.addEventListener(MENTION_COUNT_REFRESH_EVENT, handleRefreshEvent);
    return () => {
      window.removeEventListener(MENTION_COUNT_REFRESH_EVENT, handleRefreshEvent);
    };
  }, [fetchMentionCount]);

  // Listen for WebSocket events
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewMention = (_data: TicketMentionEvent) => {
      // Increment count when we receive a new mention
      setMentionCount(prev => prev + 1);
    };

    const handleMentionRead = () => {
      // Refetch when mentions are marked as read (could be from another tab)
      fetchMentionCount();
    };

    socket.on('ticket:mention', handleNewMention);
    socket.on('ticket:mentions-read', handleMentionRead);

    return () => {
      socket.off('ticket:mention', handleNewMention);
      socket.off('ticket:mentions-read', handleMentionRead);
    };
  }, [socket, isConnected, fetchMentionCount]);

  return {
    mentionCount,
    isLoading,
    error,
    refetch: refetchAndBroadcast,
  };
};

export default useTicketMentionCount;
